import {parseNotesColumnValue, type Note} from "~/domainModels/notes";
import {formatRichTextForActivityDisplay} from "~/modules/activityTimeline/utils/activityTimelineRichTextUtils";

function formatNoteBodyForDisplay(markdown: string): string {
    const trimmed = formatRichTextForActivityDisplay(markdown);
    if (trimmed === "") {
        return "-";
    }
    return trimmed;
}

function summarizeNotesForDisplay(notes: Array<Note>): string {
    if (notes.length === 0) {
        return "-";
    }
    return notes.map((note) => formatNoteBodyForDisplay(note.bodyMarkdown)).join("; ");
}

function isNoteLikeRecord(value: unknown): boolean {
    return (
        typeof value === "object" &&
        value !== null &&
        ("bodyMarkdown" in value || "body_markdown" in value || "body" in value)
    );
}

function isNotesColumnValue(value: unknown): boolean {
    if (value === null || value === undefined) {
        return false;
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed.startsWith("[") !== true && trimmed.startsWith("{") !== true) {
            return false;
        }
        return parseNotesColumnValue(value).length > 0;
    }
    if (Array.isArray(value)) {
        return value.some((item) => isNoteLikeRecord(item));
    }
    if (typeof value === "object") {
        return Object.values(value as Record<string, unknown>).some((item) => isNoteLikeRecord(item));
    }
    return false;
}

export function formatNotesValueForDisplay(value: unknown): string | null {
    if (isNotesColumnValue(value) !== true) {
        return null;
    }
    return summarizeNotesForDisplay(parseNotesColumnValue(value));
}

export function resolveNotesFieldChange(
    oldValue: unknown,
    newValue: unknown,
): {oldValue: string; newValue: string} | null {
    const oldNotes = parseNotesColumnValue(oldValue);
    const newNotes = parseNotesColumnValue(newValue);

    const oldById = new Map(oldNotes.map((note) => [note.id, note]));
    const newById = new Map(newNotes.map((note) => [note.id, note]));

    for (const [, newNote] of newById) {
        const oldNote = oldById.get(newNote.id);
        if (oldNote !== undefined && oldNote.bodyMarkdown.trim() !== newNote.bodyMarkdown.trim()) {
            return {
                oldValue: formatNoteBodyForDisplay(oldNote.bodyMarkdown),
                newValue: formatNoteBodyForDisplay(newNote.bodyMarkdown),
            };
        }
    }

    for (const [, newNote] of newById) {
        if (oldById.has(newNote.id) !== true) {
            return {
                oldValue: "-",
                newValue: formatNoteBodyForDisplay(newNote.bodyMarkdown),
            };
        }
    }

    for (const [, oldNote] of oldById) {
        if (newById.has(oldNote.id) !== true) {
            return {
                oldValue: formatNoteBodyForDisplay(oldNote.bodyMarkdown),
                newValue: "-",
            };
        }
    }

    const oldSummary = summarizeNotesForDisplay(oldNotes);
    const newSummary = summarizeNotesForDisplay(newNotes);
    if (oldSummary === newSummary) {
        return null;
    }
    return {oldValue: oldSummary, newValue: newSummary};
}
